import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from '../hooks/useTranslation';
import { fetchCities, fetchStreets } from '../utils/israelAddressApi';
import type { ShippingAddress } from '../utils/checkoutShipping';

interface CheckoutShippingAddressProps {
  value: ShippingAddress;
  onChange: (address: ShippingAddress) => void;
  showErrors?: boolean;
}

export const CheckoutShippingAddress: React.FC<CheckoutShippingAddressProps> = ({
  value,
  onChange,
  showErrors = false,
}) => {
  const { t } = useTranslation();
  const [cities, setCities] = useState<string[]>([]);
  const [streets, setStreets] = useState<string[]>([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [loadingStreets, setLoadingStreets] = useState(false);
  const [streetInput, setStreetInput] = useState(value.street);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    setStreetInput(value.street);
  }, [value.street]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingCities(true);
        const cityList = await fetchCities();
        if (!cancelled) {
          setCities(cityList);
        }
      } catch (error) {
        console.error('Failed to load cities:', error);
      } finally {
        if (!cancelled) {
          setLoadingCities(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadStreets = useDebouncedCallback(async (city: string, search: string) => {
    if (!city) {
      setStreets([]);
      return;
    }
    try {
      setLoadingStreets(true);
      const streetList = await fetchStreets(city, search || undefined);
      setStreets(streetList);
    } catch (error) {
      console.error('Failed to load streets:', error);
      setStreets([]);
    } finally {
      setLoadingStreets(false);
    }
  }, 300);

  useEffect(() => {
    if (value.city) {
      loadStreets(value.city, streetInput);
    } else {
      setStreets([]);
    }
  }, [value.city, streetInput, loadStreets]);

  const updateField = useCallback(
    (field: keyof ShippingAddress, fieldValue: string) => {
      onChange({ ...valueRef.current, [field]: fieldValue });
    },
    [onChange]
  );

  const commitStreet = useCallback(
    (street: string) => {
      const trimmed = street.trim();
      setStreetInput(trimmed);
      onChange({ ...valueRef.current, street: trimmed });
    },
    [onChange]
  );

  const handleCityChange = (_: unknown, newCity: string | null) => {
    onChange({
      ...value,
      city: newCity || '',
      street: '',
    });
    setStreetInput('');
    setStreets([]);
  };

  const fieldError = (field: keyof ShippingAddress) =>
    showErrors && !value[field]?.trim();

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        {t('checkout.shipping.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('checkout.shipping.subtitle')}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('checkout.shipping.fullName')}
            value={value.fullName}
            onChange={(e) => updateField('fullName', e.target.value)}
            required
            error={fieldError('fullName')}
            helperText={
              fieldError('fullName') ? t('checkout.shipping.required') : undefined
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={cities}
            value={value.city || null}
            onChange={handleCityChange}
            loading={loadingCities}
            disabled={loadingCities}
            isOptionEqualToValue={(option, val) => option === val}
            getOptionLabel={(option) => option}
            filterOptions={(options, { inputValue }) =>
              options.filter((o) =>
                o.toLowerCase().includes(inputValue.trim().toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('checkout.shipping.city')}
                required
                error={fieldError('city')}
                helperText={fieldError('city') ? t('checkout.shipping.required') : undefined}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCities ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={streets}
            value={value.street || null}
            onChange={(_, newStreet) => {
              commitStreet(typeof newStreet === 'string' ? newStreet : '');
            }}
            onInputChange={(_, newInput, reason) => {
              setStreetInput(newInput);
              if (reason === 'input' || reason === 'clear') {
                commitStreet(newInput);
              }
            }}
            onBlur={() => {
              if (streetInput.trim() !== valueRef.current.street.trim()) {
                commitStreet(streetInput);
              }
            }}
            inputValue={streetInput}
            loading={loadingStreets}
            disabled={!value.city || loadingCities}
            freeSolo
            isOptionEqualToValue={(option, val) => option === val}
            getOptionLabel={(option) => option}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('checkout.shipping.street')}
                required
                error={fieldError('street')}
                helperText={fieldError('street') ? t('checkout.shipping.required') : undefined}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingStreets ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              !value.city
                ? t('checkout.shipping.selectCityFirst')
                : t('checkout.shipping.noStreets')
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={t('checkout.shipping.streetNumber')}
            value={value.streetNumber}
            onChange={(e) => updateField('streetNumber', e.target.value)}
            required
            error={fieldError('streetNumber')}
            helperText={
              fieldError('streetNumber') ? t('checkout.shipping.required') : undefined
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={t('checkout.shipping.flat')}
            value={value.flat || ''}
            onChange={(e) => updateField('flat', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={t('checkout.shipping.phone')}
            type="tel"
            value={value.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            required
            error={fieldError('phone')}
            helperText={fieldError('phone') ? t('checkout.shipping.required') : undefined}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('checkout.shipping.notes')}
            value={value.notes || ''}
            onChange={(e) => updateField('notes', e.target.value)}
            multiline
            minRows={2}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
